import React, { useEffect, useState } from "react";
import { api, API } from "../lib/api";
import { useSiteContext } from "../components/layout/Layout";
import { Button } from "../components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, Legend, CartesianGrid } from "recharts";
import { Download, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

const RANGES = [
  { v: 1, label: "Last 1h" },
  { v: 6, label: "Last 6h" },
  { v: 24, label: "Last 24h" },
  { v: 168, label: "Last 7d" },
  { v: 720, label: "Last 30d" },
];

function ChartCard({ title, subtitle, children, onExport }) {
  return (
    <div className="glass p-5 rounded-lg">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="font-semibold text-gray-900">{title}</h3>
          {subtitle && <p className="text-[11px] text-gray-500 mt-0.5">{subtitle}</p>}
        </div>
        {onExport && (
          <Button variant="ghost" size="sm" onClick={onExport} className="text-gray-500 hover:text-orange-400">
            <Download className="w-3.5 h-3.5 mr-1" strokeWidth={2.5} /> Export
          </Button>
        )}
      </div>
      {children}
    </div>
  );
}

export default function GraphicalDashboard() {
  const { selectedSite } = useSiteContext();
  const [hours, setHours] = useState(24);
  const [hist, setHist] = useState([]);

  useEffect(() => {
    if (!selectedSite) return;
    api.get(`/telemetry/${selectedSite}/history`, { params: { hours } }).then((r) => {
      setHist(r.data.map((x) => ({
        ...x,
        time: new Date(x.ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
      })));
    });
  }, [selectedSite, hours]);

  const pumpHours = [
    { name: "Main", run: 112 },
    { name: "Standby", run: 8 },
    { name: "Jockey", run: 340 },
    { name: "Booster", run: 24 },
  ];
  const uptime = [{ name: "Up", value: 99.2 }, { name: "Down", value: 0.8 }];

  const downloadPDF = async () => {
    try {
      const token = localStorage.getItem("f360_token");
      const res = await fetch(`${API}/reports/pdf/${selectedSite}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (!res.ok) throw new Error("fail");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url);
      toast.success("GR Compliance PDF generated");
    } catch (e) {
      toast.error("PDF export failed");
    }
  };

  const downloadExcel = async () => {
    try {
      const token = localStorage.getItem("f360_token");
      const res = await fetch(`${API}/reports/excel/${selectedSite}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "fire360_report.xlsx";
      a.click();
      toast.success("Excel downloaded");
    } catch {
      toast.error("Excel export failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="graphical-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">Graphical Dashboard</h1>
          <p className="text-gray-500 text-sm mt-1">Trends, pump cycles, tank history, alarm frequency & uptime</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={String(hours)} onValueChange={(v) => setHours(Number(v))}>
            <SelectTrigger className="w-36 bg-gray-50 border-gray-200" data-testid="range-selector">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white border-gray-200 text-gray-900">
              {RANGES.map((r) => <SelectItem key={r.v} value={String(r.v)}>{r.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={downloadPDF} className="f360-gradient border-0" data-testid="export-pdf-btn"><Download className="w-4 h-4 mr-1" strokeWidth={2.5} />PDF</Button>
          <Button onClick={downloadExcel} variant="outline" className="border-gray-200" data-testid="export-excel-btn"><FileSpreadsheet className="w-4 h-4 mr-1" strokeWidth={2.5} />Excel</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Hydrant Riser Pressure" subtitle="GR threshold 3.5 Bar">
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={hist}>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" strokeDasharray="3 3" />
              <XAxis dataKey="time" stroke="#64748b" fontSize={10} />
              <YAxis domain={[0, 10]} stroke="#64748b" fontSize={10} />
              <Tooltip contentStyle={{ background: "#0a0f1e", border: "1px solid #333" }} />
              <ReferenceLine y={3.5} stroke="#ff3366" strokeDasharray="4 4" />
              <Line type="monotone" dataKey="hydrant_pressure_bar" stroke="#ff6b35" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Sprinkler Riser Pressure" subtitle="GR threshold 5 Bar">
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={hist}>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" strokeDasharray="3 3" />
              <XAxis dataKey="time" stroke="#64748b" fontSize={10} />
              <YAxis domain={[0, 10]} stroke="#64748b" fontSize={10} />
              <Tooltip contentStyle={{ background: "#0a0f1e", border: "1px solid #333" }} />
              <ReferenceLine y={5} stroke="#ff3366" strokeDasharray="4 4" />
              <Line type="monotone" dataKey="sprinkler_pressure_bar" stroke="#4fd1ff" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Water Tank Levels" subtitle="UG & Overhead trends (%)">
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={hist}>
              <defs>
                <linearGradient id="g1" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stopColor="#4fd1ff" stopOpacity={0.6} /><stop offset="100%" stopColor="#4fd1ff" stopOpacity={0.05} /></linearGradient>
                <linearGradient id="g2" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stopColor="#ff3366" stopOpacity={0.5} /><stop offset="100%" stopColor="#ff3366" stopOpacity={0.05} /></linearGradient>
              </defs>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" strokeDasharray="3 3" />
              <XAxis dataKey="time" stroke="#64748b" fontSize={10} />
              <YAxis domain={[0, 100]} stroke="#64748b" fontSize={10} />
              <Tooltip contentStyle={{ background: "#0a0f1e", border: "1px solid #333" }} />
              <Legend />
              <Area type="monotone" dataKey="ug_tank_pct" stroke="#4fd1ff" fill="url(#g1)" name="UG Tank" />
              <Area type="monotone" dataKey="oht_tank_pct" stroke="#ff3366" fill="url(#g2)" name="Overhead" />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Diesel Tank Trend" subtitle="Alert < 20%">
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={hist}>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" strokeDasharray="3 3" />
              <XAxis dataKey="time" stroke="#64748b" fontSize={10} />
              <YAxis domain={[0, 100]} stroke="#64748b" fontSize={10} />
              <Tooltip contentStyle={{ background: "#0a0f1e", border: "1px solid #333" }} />
              <ReferenceLine y={20} stroke="#ef4444" strokeDasharray="4 4" />
              <Line type="monotone" dataKey="diesel_tank_pct" stroke="#f59e0b" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Pump Run Hours (30d)" subtitle="Cumulative operation time">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={pumpHours}>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" strokeDasharray="3 3" />
              <XAxis dataKey="name" stroke="#64748b" fontSize={11} />
              <YAxis stroke="#64748b" fontSize={10} />
              <Tooltip contentStyle={{ background: "#0a0f1e", border: "1px solid #333" }} />
              <Bar dataKey="run" fill="#ff6b35" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Gateway Uptime" subtitle="Target 99% SLA">
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={uptime} cx="50%" cy="50%" innerRadius={60} outerRadius={90} dataKey="value" stroke="none">
                <Cell fill="#22c55e" />
                <Cell fill="#ef4444" />
              </Pie>
              <Tooltip contentStyle={{ background: "#0a0f1e", border: "1px solid #333" }} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );
}
